import React from "react";

/**
 * Button Component
 *
 * A reusable button component with consistent styling and variants.
 * Supports different colors, sizes, and states.
 *
 * @param {ReactNode} children - Button content
 * @param {function} onClick - Click handler function
 * @param {string} variant - Button style variant ('primary', 'secondary', 'outline', 'ghost')
 * @param {string} color - Color theme ('blue', 'violet', 'gray', 'green')
 * @param {string} size - Button size ('sm', 'md', 'lg')
 * @param {boolean} disabled - Whether the button is disabled
 * @param {string} className - Optional additional CSS classes
 * @param {object} ...props - Any other props to pass to the button element
 */
const Button = ({
  children,
  onClick,
  variant = "primary",
  color = "violet",
  size = "md",
  disabled = false,
  className = "",
  ...props
}) => {
  // Size classes
  const sizeClasses = {
    sm: "px-4 py-2 text-sm",
    md: "px-6 py-3 text-base",
    lg: "px-8 py-4 text-lg",
  };

  // Color and variant combinations
  const variantClasses = {
    primary: {
      blue: "bg-blue-500 hover:bg-blue-600 text-white",
      violet: "bg-violet-500 hover:bg-violet-600 text-white",
      gray: "bg-gray-500 hover:bg-gray-600 text-white",
      green: "bg-green-500 hover:bg-green-600 text-white",
    },
    secondary: {
      blue: "bg-blue-600 hover:bg-blue-700 text-white",
      violet: "bg-violet-600 hover:bg-violet-700 text-white",
      gray: "bg-gray-600 hover:bg-gray-700 text-white",
      green: "bg-green-600 hover:bg-green-700 text-white",
    },
    outline: {
      blue: "border-2 border-blue-500 text-blue-500 hover:bg-blue-500 hover:text-white",
      violet:
        "border-2 border-violet-500 text-violet-500 hover:bg-violet-500 hover:text-white",
      gray: "border-2 border-gray-500 text-gray-500 hover:bg-gray-500 hover:text-white",
      green:
        "border-2 border-green-500 text-green-500 hover:bg-green-500 hover:text-white",
    },
    ghost: {
      blue: "text-blue-500 hover:bg-blue-500/10 hover:text-blue-400",
      violet: "text-violet-500 hover:bg-violet-500/10 hover:text-violet-400",
      gray: "text-gray-500 hover:bg-gray-500/10 hover:text-gray-400",
      green: "text-green-500 hover:bg-green-500/10 hover:text-green-400",
    },
  };

  const baseClasses =
    "rounded-full transition-colors duration-300 font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed";

  const classes = `
    ${baseClasses}
    ${sizeClasses[size]}
    ${variantClasses[variant][color]}
    ${className}
  `.trim();

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={classes}
      {...props}
    >
      {children}
    </button>
  );
};

export default Button;
